/** Pure helpers shared by the search filter bar effects. */
import { MultiselectProps } from '@amzn/awsui-components-console';
import type { SearchFilters } from 'src/api/graphql/searchTypes';
import {
  MANDATORY_NAMESPACE_FILTER_MESSAGE_ID,
  MANDATORY_NAMESPACE_FILTER_THRESHOLD,
  MIN_QUERY_LENGTH,
  MIN_QUERY_LENGTH_MESSAGE_ID,
  MIN_SEARCH_CRITERIA_MESSAGE_ID,
  TRANSLATION_LOCALE_REQUIRED_MESSAGE_ID,
} from './constants';
import type { StringGroupItem } from './searchFilterData';
import type { TranslateFn } from 'src/i18n/shim/useTranslation';

/** The setter signature `SearchFilterBar` receives from its parent. */
export type FiltersUpdater = (
  update: SearchFilters | ((prev: SearchFilters) => SearchFilters),
) => void;

/**
 * Filter keys holding a list of ids that is derived from a parent selection and
 * therefore has to be pruned when that parent selection changes.
 */
export type DependentFilterKey = 'stringGroupIds' | 'cxIds';

/** String groups fetched for one namespace, plus whether the fetch was capped. */
export interface CachedStringGroups {
  readonly groups: readonly StringGroupItem[];
  readonly truncated: boolean;
}

/**
 * Drops selected ids under `filterKey` that are no longer offered by
 * `validOptions`. Shared by the String Group and CXID paths, which have
 * identical semantics:
 * - Widening the parent selection preserves valid selections
 * - Narrowing it drops only the now-invalid ids
 * - Dropping every id clears the key (undefined) rather than leaving `[]`
 * - No-op when there is nothing to drop
 *
 * The read + check + compute happens entirely inside the functional updater so
 * that a fetch which resolves after the user changed their selection prunes
 * against the latest state rather than a stale render-time closure.
 */
export function pruneInvalidSelections(
  onFiltersChange: FiltersUpdater,
  filterKey: DependentFilterKey,
  validOptions: readonly MultiselectProps.Option[],
): void {
  onFiltersChange((prev) => {
    const selected = prev[filterKey];
    if (!selected?.length) return prev;
    const validValues = new Set(validOptions.map((opt) => opt.value));
    const pruned = selected.filter((id) => validValues.has(id));
    return pruned.length < selected.length
      ? { ...prev, [filterKey]: pruned.length ? pruned : undefined }
      : prev;
  });
}

/**
 * Builds the union of string group options across the selected namespaces.
 * Dedupes by stringGroupId and stable-sorts alphabetically.
 */
export function buildStringGroupUnion(
  namespaces: readonly string[],
  cache: ReadonlyMap<string, CachedStringGroups>,
): MultiselectProps.Option[] {
  const seen = new Set<string>();
  const allGroups: StringGroupItem[] = [];

  for (const ns of namespaces) {
    const groups = cache.get(ns)?.groups || [];
    for (const g of groups) {
      if (!seen.has(g.stringGroupId)) {
        seen.add(g.stringGroupId);
        allGroups.push(g);
      }
    }
  }

  return allGroups
    .sort((a, b) => a.stringGroupId.localeCompare(b.stringGroupId))
    .map((g) => ({ label: g.stringGroupId, value: g.stringGroupId }));
}

/**
 * True when any of the selected namespaces had its string group fetch capped —
 * drives the truncation hint on the String Group dropdown.
 */
export function hasTruncatedStringGroups(
  namespaces: readonly string[],
  cache: ReadonlyMap<string, CachedStringGroups>,
): boolean {
  return namespaces.some((ns) => cache.get(ns)?.truncated === true);
}

/** Stable "nothing selected" filters object used when clearing. */
export const NO_FILTERS: SearchFilters = {};

/**
 * True when the user has narrowed the search in any way — every dropdown filter,
 * plus the exact-match toggle and the free-text query, all of which Clear resets.
 *
 * Takes the individual pieces rather than the whole `SearchState` so this module
 * stays free of a dependency on the page component. Do not reuse it to gate search
 * submission — see `hasMinimumSearchCriteria` below.
 */
export function hasActiveFilters(
  filters: SearchFilters,
  queryText: string,
  exactMatch: boolean,
): boolean {
  if (queryText.trim().length > 0) return true;
  if (exactMatch) return true;
  return Object.values(filters).some(
    (value) => Array.isArray(value) && value.length > 0,
  );
}

/**
 * Filter keys that actually NARROW a source-string search, and therefore count as
 * a search criterion.
 *
 * `translatedLocales` is deliberately absent: it exists only in translation mode,
 * which has its own required-filter validation, so listing it here would quietly
 * extend the minimum-criteria rule to translation search.
 *
 * Mirrors the dimensions the backend counts for a source-string search
 * (`SourceSearchFilters.hasAnyFilter()`); keep the two in step, or the UI will block
 * a search the API would accept. The backend also counts `clientIds`, which the
 * GraphQL `SearchFilters` input does not expose, so the UI cannot send it.
 */
const CRITERION_FILTER_KEYS = [
  'namespaceIds',
  'statuses',
  'stringGroupIds',
  'cxIds',
  'cxFamilies',
] as const satisfies readonly (keyof SearchFilters)[];

/**
 * True when the user has supplied something to search on: a query of at least
 * `MIN_QUERY_LENGTH` characters, or one populated narrowing filter.
 *
 * Must not be merged with `hasActiveFilters`: that one counts `exactMatch`
 * because Clear resets it, whereas `exactMatch` only changes how `queryText` is
 * matched and so narrows nothing by itself.
 *
 * Not a UI-only rule: the backend rejects a search constrained by neither query text
 * nor a filter (`requireConstrainedSearch` → 400 ValidationException, a blank
 * `queryText` counting as omitted), so this check only keeps a doomed request off the
 * wire and names the reason inline. An empty `queryText` stays valid once a filter is
 * set; only the `MIN_QUERY_LENGTH` floor is ours alone.
 */
export function hasMinimumSearchCriteria(
  filters: SearchFilters,
  queryText: string,
): boolean {
  if (queryText.trim().length >= MIN_QUERY_LENGTH) return true;
  return CRITERION_FILTER_KEYS.some((key) => (filters[key]?.length ?? 0) > 0);
}

/**
 * A veto rather than a criterion: a too-short term reaches the analyzer alongside
 * any filters, so callers must refuse the search even when
 * `hasMinimumSearchCriteria` passes. Applies in translation mode too, which has no
 * minimum-criteria rule of its own.
 *
 * Blank is deliberately not too short — a filter-only search is supported, and
 * flagging it would put a warning on the pristine page.
 */
export function isQueryTooShort(queryText: string): boolean {
  const trimmedLength = queryText.trim().length;
  return trimmedLength > 0 && trimmedLength < MIN_QUERY_LENGTH;
}

/**
 * Why a search may not run, as a code rather than a message, so the predicate stays copy-free:
 * `canAutoRunSearch` only asks whether a shared link is runnable, and `SearchPanelFooter` branches
 * on the reason — which a code keeps locale-independent.
 */
export type SearchGateCode =
  | 'QUERY_TOO_SHORT'
  | 'NAMESPACE_REQUIRED'
  | 'TRANSLATED_LOCALE_REQUIRED'
  | 'MIN_CRITERIA';

/** Resource id carrying the message for each gate code. */
const SEARCH_GATE_MESSAGE_IDS: Record<SearchGateCode, string> = {
  QUERY_TOO_SHORT: MIN_QUERY_LENGTH_MESSAGE_ID,
  NAMESPACE_REQUIRED: MANDATORY_NAMESPACE_FILTER_MESSAGE_ID,
  TRANSLATED_LOCALE_REQUIRED: TRANSLATION_LOCALE_REQUIRED_MESSAGE_ID,
  MIN_CRITERIA: MIN_SEARCH_CRITERIA_MESSAGE_ID,
};

/** The blocking reason code, or null when runnable. Shared by the table's runtime gate and the
 * page's auto-run. */
export function searchGateCode(
  filters: SearchFilters,
  queryText: string,
  isSource: boolean,
  requireNamespaceFilter = false,
): SearchGateCode | null {
  if (isQueryTooShort(queryText)) return 'QUERY_TOO_SHORT';
  // Large-access tenants must scope to a namespace first (both modes, before per-mode rules).
  if (requireNamespaceFilter && !(filters.namespaceIds?.length ?? 0)) {
    return 'NAMESPACE_REQUIRED';
  }
  if (!isSource) {
    return (filters.translatedLocales?.length ?? 0) > 0
      ? null
      : 'TRANSLATED_LOCALE_REQUIRED';
  }
  return hasMinimumSearchCriteria(filters, queryText) ? null : 'MIN_CRITERIA';
}

/** The localized message for a gate code, with that code's own placeholders applied. */
export function searchGateMessage(
  t: TranslateFn,
  code: SearchGateCode,
): string {
  return t(SEARCH_GATE_MESSAGE_IDS[code], {
    min: MIN_QUERY_LENGTH,
    threshold: MANDATORY_NAMESPACE_FILTER_THRESHOLD,
  });
}

/** Use {@link searchGateCode} where only the reason, not its wording, matters. */
export function searchCriteriaWarning(
  t: TranslateFn,
  filters: SearchFilters,
  queryText: string,
  isSource: boolean,
  requireNamespaceFilter = false,
): string | null {
  const code = searchGateCode(
    filters,
    queryText,
    isSource,
    requireNamespaceFilter,
  );
  return code === null ? null : searchGateMessage(t, code);
}
